import React, { useContext, useEffect, useState } from "react";
import {
    Form,
    LottieLoadingIndicator,
    PortalFormActions,
    PortalFormScrollBody,
    PortalFormSection,
    PortalFormSectionDescription,
    PortalFormSectionHeader,
    PortalFormSectionTitle,
    PortalFormSurface,
    StandardModalActionButton,
    StandardModalActionGroup,
    Toast
} from "src/components";
import { emptyContentHtml } from "src/components/RichTextEditor/adapters";
import LeftSideContainer from "./leftContainer/LeftSideContainer";
import RightSideContainer from "./rightContainer/RightSideContainer";
import { NewCampaignContext } from "./NewCampaignContext";
import { EMAIL_STATUS, SEND_TYPE, SIDEBAR_LIST } from "../../constants";
import { EmailCampaignsContext } from "../../EmailCampaignsContext";
import { s3PresignedEmailAttachmentUrl, s3PresignedHeaderImageUrl, saveEmailCampaign } from "../../api/newCampaignApi";
import { updateEmail } from "../../api/emailCampaignsApi";

export default function NewCampaign() {
    const { closeEmailForm, selectedCampaign, initialFetchSettings, setCurrentSidebar } = useContext(EmailCampaignsContext);
    const [campaignName, setCampaignName] = useState("");
    const [emailSubject, setEmailSubject] = useState("");
    const [recipientContactUuids, setRecipientContactUuids] = useState([]);
    const [selectionEditAudience, setSelectionEditAudience] = useState(null);
    const [selectionEditDialogShown, setSelectionEditDialogShown] = useState(false);
    const [emailImageHeader, setEmailImageHeader] = useState(null);
    const [emailContent, setEmailContent] = useState(selectedCampaign?.emailContent || emptyContentHtml);
    const [attachments, setAttachments] = useState([]);
    const [sendType, setSendType] = useState(SEND_TYPE.SCHEDULE);
    const [sendSchedule, setSendSchedule] = useState(null);
    const [errors, setErrors] = useState({});
    const [backgroundImage, setBackgroundImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Manual validation
    const validate = status => {
        const newErrors = {};
        if (!campaignName.trim()) newErrors.campaignName = "Campaign name is required.";
        if (!emailSubject.trim()) newErrors.emailSubject = "Email subject is required.";
        if (status !== EMAIL_STATUS.DRAFT) {
            if (!recipientContactUuids) newErrors.recipients = "Recipients are required.";
            if (!emailContent || emailContent === emptyContentHtml) newErrors.emailContent = "Email content is required.";
            if (sendType === SEND_TYPE.SCHEDULE && sendSchedule === null) newErrors.dateTime = "Date & time is required.";
        }
        return newErrors;
    };

    const buildBasePayload = (status) => ({
        campaignName,
        emailSubject,
        emailContent,
        scheduleAt: sendType === SEND_TYPE.SCHEDULE ? sendSchedule : null,
        contactUuids: recipientContactUuids,
        emailStatus:
            status === EMAIL_STATUS.DRAFT
                ? EMAIL_STATUS.DRAFT
                : sendType === SEND_TYPE.SCHEDULE
                    ? EMAIL_STATUS.SCHEDULED
                    : EMAIL_STATUS.SENT
    });

    const handleHeaderImage = async (payload, { isUpdate = false } = {}) => {
        if (emailImageHeader) {
            const fileExtension = emailImageHeader.type.split("/").pop();
            const presigned = await s3PresignedHeaderImageUrl(
                emailImageHeader,
                fileExtension,
                "image"
            );
            payload.headerImageUrl = presigned.storedUrl.split("?")[0];
            return;
        }

        if (isUpdate && selectedCampaign) {
            payload.headerImageUrl = backgroundImage
                ? selectedCampaign.headerImageUrl
                : null;
        }
    };

    const handleAttachments = async (payload, { allowExisting = false } = {}) => {
        if (attachments.length === 0) return;

        payload.attachments = [];

        for (const attachment of attachments) {
            const file = attachment.file;

            if (!file && allowExisting) {
                payload.attachments.push({
                    attachmentUuid: attachment.uuid
                });
                continue;
            }

            if (!file) continue;

            const fileExtension = file.type.split("/").pop();

            let uploadType = "file";
            if (file.type.startsWith("image/")) uploadType = "image";
            else if (file.type === "application/pdf") uploadType = "pdf";
            else if (file.type.includes("word")) uploadType = "doc";
            else if (file.type.includes("excel")) uploadType = "xls";

            const presigned = await s3PresignedEmailAttachmentUrl(
                file,
                fileExtension,
                uploadType
            );

            payload.attachments.push({
                filename: file.name,
                s3Url: presigned.storedUrl.split("?")[0],
            });
        }
    };

    const handleSave = async ({
        status,
        isUpdate,
        onSave
    }) => {
        const newErrors = validate(status);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsLoading(true);

        try {
            const payload = buildBasePayload(status);

            await handleHeaderImage(payload, { isUpdate });
            await handleAttachments(payload, { allowExisting: isUpdate });

            await onSave(payload);

            if (status === EMAIL_STATUS.DRAFT) {
                setCurrentSidebar(SIDEBAR_LIST.DRAFT);
            } else {
                if (sendType === SEND_TYPE.SCHEDULE) {
                    setCurrentSidebar(SIDEBAR_LIST.SCHEDULED);
                }
                if (sendType === SEND_TYPE.SEND_NOW) {
                    setCurrentSidebar(SIDEBAR_LIST.SENT);
                }
            }

            closeEmailForm();
            initialFetchSettings();
        } catch (error) {
            console.error(error);
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = (status) =>
        handleSave({
            status,
            isUpdate: false,
            onSave: (payload) => saveEmailCampaign(payload)
        });

    const onUpdate = (status) => {
        handleSave({
            status,
            isUpdate: true,
            onSave: (payload) => updateEmail(selectedCampaign.uuid, payload)
        });
    };

    const openEditSelection = audience => {
        setSelectionEditAudience(audience);
        setSelectionEditDialogShown(true);
    };

    const closeEditSelection = () => {
        setSelectionEditAudience(null);
        setSelectionEditDialogShown(false);
    };

    useEffect(() => {
        if (emailContent && emailContent !== emptyContentHtml) {
            setErrors({ ...errors, emailContent: "" });
        }
    }, [emailContent]);

    const validateForSendTestEmail = (setShowTestEmails) => {
        // email status draft to escape select date time validation
        const newErrors = validate(EMAIL_STATUS.DRAFT);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors); // display errors
            return;
        }
        setShowTestEmails(true);

    };

    useEffect(() => {
        if (selectedCampaign) {
            const contactUuids = selectedCampaign?.recipientContacts?.map(contact => contact.uuid);
            setCampaignName(selectedCampaign?.campaignName);
            setEmailSubject(selectedCampaign?.emailSubject);
            setRecipientContactUuids(contactUuids || []);
            setBackgroundImage(selectedCampaign?.headerImageUrl);
            setEmailContent(selectedCampaign?.emailContent || emptyContentHtml);
            setAttachments(selectedCampaign?.attachments);
            if (selectedCampaign.scheduleAt) {
                setSendSchedule(new Date(selectedCampaign.scheduleAt));
            }
        }
    }, [selectedCampaign]);

    return (
        <NewCampaignContext.Provider
            value={{
                campaignName,
                setCampaignName,
                emailSubject,
                setEmailSubject,
                recipientContactUuids,
                setRecipientContactUuids,
                openEditSelection,
                closeEditSelection,
                selectionEditAudience,
                selectionEditDialogShown,
                emailImageHeader,
                setEmailImageHeader,
                emailContent,
                setEmailContent,
                backgroundImage,
                setBackgroundImage,
                attachments,
                setAttachments,
                sendType,
                setSendType,
                sendSchedule,
                setSendSchedule,
                errors,
                setErrors
            }}
        >
            <PortalFormSurface maxWidth="94.5rem" marginTop="0" flex="1 1 auto" minHeight="0">
                <Form
                    width="100%"
                    height="100%"
                    paddingBottom="0"
                    onSubmit={(event) => { event.preventDefault(); }}
                    style={{ margin: 0, display: "flex", flexDirection: "column", minHeight: 0 }}
                >
                    <PortalFormScrollBody
                        columns="minmax(0, 1fr) minmax(20rem, 0.7fr)"
                        padding="1.25rem"
                        gap="1rem"
                        alignItems="start"
                    >
                        <PortalFormSection>
                            <PortalFormSectionHeader withDivider>
                                <PortalFormSectionTitle>
                                    Campaign Content
                                </PortalFormSectionTitle>
                                <PortalFormSectionDescription>
                                    Name the campaign, choose recipients, compose the email and set delivery timing.
                                </PortalFormSectionDescription>
                            </PortalFormSectionHeader>
                            <LeftSideContainer validateForSendTestEmail={validateForSendTestEmail} />
                        </PortalFormSection>

                        <PortalFormSection>
                            <PortalFormSectionHeader withDivider>
                                <PortalFormSectionTitle>
                                    Campaign Preview
                                </PortalFormSectionTitle>
                                <PortalFormSectionDescription>
                                    Review how the email content will appear before sending.
                                </PortalFormSectionDescription>
                            </PortalFormSectionHeader>
                            <RightSideContainer />
                        </PortalFormSection>
                    </PortalFormScrollBody>

                    <PortalFormActions sticky={false}>
                        <StandardModalActionGroup $pushRight>
                            <StandardModalActionButton type="button" onClick={() => closeEmailForm(false)} disabled={isLoading}>
                                Cancel
                            </StandardModalActionButton>
                            <StandardModalActionButton
                                type="button"
                                disabled={isLoading}
                                onClick={selectedCampaign ?
                                    () => onUpdate(EMAIL_STATUS.DRAFT) :
                                    () => onSubmit(EMAIL_STATUS.DRAFT)}
                            >
                                {isLoading
                                    ? <LottieLoadingIndicator height="1.5rem" width="1.5rem" containerHeight="1.5rem" margin="0" />
                                    : "Save Draft"}
                            </StandardModalActionButton>
                            <StandardModalActionButton
                                type="button"
                                $variant="primary"
                                disabled={isLoading}
                                onClick={selectedCampaign ?
                                    () => onUpdate(EMAIL_STATUS.SENT) :
                                    () => onSubmit(EMAIL_STATUS.SENT)}
                            >
                                {isLoading
                                    ? <LottieLoadingIndicator height="1.5rem" width="1.5rem" containerHeight="1.5rem" margin="0" />
                                    : sendType === SEND_TYPE.SCHEDULE ? "Schedule" : "Send Now"}
                            </StandardModalActionButton>
                        </StandardModalActionGroup>
                    </PortalFormActions>
                </Form>
            </PortalFormSurface>
        </NewCampaignContext.Provider>
    );
}
